
/* ============================== src/hooks/useSort.js ============================== */
import { useCallback } from "react";

/**
 * Контроль сортировки поверх useFilters.
 *
 * Все вьюхи со списком/канбаном уже держат фильтры в useFilters. Поля
 * сортировки - такие же поля состояния фильтра, и живут там же:
 * два поля (sortField / sortDir) в INITIAL_FILTERS, обновление через
 * setFilter. Иначе сброс фильтров («resetFilters») не откатывал бы
 * сортировку к дефолту, а состояние разъехалось бы между двумя
 * независимыми useStates.
 *
 * Хук берёт на себя один рутинный кусок: смену поля с пересчётом
 * направления и переключение направления. Правило дефолта:
 *   - числовое поле → 'desc' (сначала самые большие);
 *   - строковое поле → 'asc' (А→Я).
 * Что считать числовым, задаёт вызывающий: Set из имён полей.
 *
 * Возвращает поле/направление (для чтения) и два колбэка (для
 * SortControl). Всё, что делает хук, - вызывает setFilter с готовыми
 * ключами; он ничего не хранит и не мемоизирует лишнего.
 */
export function useSort({ filters, setFilter, numericFields }) {
  const field = filters.sortField;
  const dir = filters.sortDir;

  return {
    field,
    dir,
    handleFieldChange: useCallback(
      (newField) => {
        setFilter("sortField", newField);
        setFilter("sortDir", numericFields.has(newField) ? "desc" : "asc");
      },
      [setFilter, numericFields],
    ),
    handleDirToggle: useCallback(() => {
      setFilter("sortDir", dir === "asc" ? "desc" : "asc");
    }, [setFilter, dir]),
  };
}

/**
 * Гарантирует наличие полей сортировки в объекте фильтров.
 *
 * Назначение: применяется к INITIAL_FILTERS во вьюхах, где сортировка
 * декларируется не всегда. Без этого хелпера useSort получит
 * `filters.sortField === undefined` → SortControl отрендерится с пустым
 * Select, а первое же `handleDirToggle` положит в стор мусор вместо
 * ожидаемого направления.
 *
 * Правила:
 *   - уже заданные поля не перетираются;
 *   - отсутствующие заполняются значениями из defaults;
 *   - если defaults не переданы - берём нейтральные "priority" / "desc".
 *
 * Чистая функция: возвращает новый объект, исходный не мутирует.
 * Порядок ключей сохраняется: сортировочные поля уходят в конец
 * (не важно для JS, важно для читаемости diff в сторе).
 *
 * @param {object}  filters                - базовый объект фильтров
 * @param {object}  [defaults]
 * @param {string}  [defaults.sortField]   - дефолтное поле сортировки
 * @param {"asc"|"desc"} [defaults.sortDir] - дефолтное направление
 * @returns {object}
 */
export function withSortDefaults(filters, defaults = {}) {
  const { sortField = "priority", sortDir = "desc" } = defaults;

  return {
    ...filters,
    sortField: filters.sortField ?? sortField,
    sortDir: filters.sortDir ?? sortDir,
  };
}
